/**
 * Products list page - all ready-made merch with category + search filtering.
 *
 * Purpose:  Display all active products; honor ?c= and ?q= URL params.
 *           Filter chips + search update the URL so state is bookmarkable and
 *           the browser back button works.
 * Inputs:   /api/products (Product[]), URL search params ?c= ?q= ?sort=
 * Outputs:  Filtered + sorted product grid; active filter reflected in URL.
 * Constraints: fetchProducts unwrap pattern preserved unchanged. ProductCard
 *              unchanged. No new API calls.
 */

import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ProductCard } from '@/components/vite/ProductCard'
import { TrustStrip } from '@/components/vite/TrustStrip'
import type { Product } from '@/lib/types'
import { STALE } from '@/lib/query-client'
import {
  CATEGORIES,
  catalogFilterStateFromParams,
  countCatalogChips,
  filterCatalogProducts,
  updateCatalogSearchParams,
  type CategoryId,
} from '@/lib/categories'

// ─── Fetch ────────────────────────────────────────────────────────────────────

async function fetchProducts(): Promise<Product[]> {
  const res = await fetch('/api/products')
  if (!res.ok) throw new Error('Failed to load products')
  const json = await res.json() as { products?: Product[] } | Product[]
  if (Array.isArray(json)) return json
  return (json as { products?: Product[] }).products ?? []
}

// ─── Legacy branch query support ──────────────────────────────────────────────

const LEGACY_BRANCH_QUERY: Record<string, string> = {
  army: 'Army',
  navy: 'Navy',
  marines: 'Marines',
  airforce: 'Air Force',
  coastguard: 'Coast Guard',
  spaceforce: 'Space Force',
  gwot: 'GWOT',
}

// ─── Sort options ──────────────────────────────────────────────────────────────

type SortKey = 'featured' | 'price-asc' | 'price-desc' | 'name-asc'

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'featured',   label: 'Featured'          },
  { value: 'price-asc',  label: 'Price: low to high' },
  { value: 'price-desc', label: 'Price: high to low' },
  { value: 'name-asc',   label: 'Name A–Z'           },
]

function normalizeSortKey(raw: string | null): SortKey {
  return SORT_OPTIONS.some((opt) => opt.value === raw) ? (raw as SortKey) : 'featured'
}

function sortProducts(products: Product[], sort: SortKey): Product[] {
  const arr = [...products]
  switch (sort) {
    case 'price-asc':  return arr.sort((a, b) => a.price_cents - b.price_cents)
    case 'price-desc': return arr.sort((a, b) => b.price_cents - a.price_cents)
    case 'name-asc':   return arr.sort((a, b) => a.name.localeCompare(b.name))
    default:           return arr // 'featured' = API order
  }
}

// ─── Category chip ─────────────────────────────────────────────────────────────

interface CategoryChipProps {
  label: string
  count: number
  active: boolean
  title: string
  onToggle: () => void
}

function CategoryChip({ label, count, active, title, onToggle }: CategoryChipProps) {
  return (
    <button
      type="button"
      aria-pressed={active}
      title={title}
      onClick={onToggle}
      className={[
        'inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold whitespace-nowrap',
        active
          ? 'bg-gold border-gold text-white shadow-sm shadow-gold/20'
          : 'bg-transparent border-white/20 text-white/70 hover:border-gold/50 hover:text-white',
      ].join(' ')}
    >
      <span>{label}</span>
      <span
        className={[
          'inline-flex min-w-6 justify-center rounded-full px-1.5 py-0.5 text-[11px] leading-none',
          active ? 'bg-white/15 text-white' : 'bg-white/8 text-gold-text',
        ].join(' ')}
      >
        {count}
      </span>
    </button>
  )
}

function isCountedCategory(id: CategoryId): id is Exclude<CategoryId, 'byom'> {
  return id !== 'byom'
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams()

  const filterState = catalogFilterStateFromParams(searchParams)
  const legacyBranchQuery = !filterState.query
    ? LEGACY_BRANCH_QUERY[searchParams.get('branch') ?? ''] ?? ''
    : ''
  const activeCategory = filterState.category === 'byom' ? 'all' : filterState.category
  const searchQuery = legacyBranchQuery || filterState.query
  const activeSort = normalizeSortKey(searchParams.get('sort'))

  const { data: products = [], isLoading, isError } = useQuery({
    queryKey: ['products'],
    queryFn: fetchProducts,
    staleTime: STALE.PRODUCTS,
  })

  const chipCounts = useMemo(() => countCatalogChips(products, searchQuery), [products, searchQuery])

  const filtered = useMemo<Product[]>(() => {
    return sortProducts(
      filterCatalogProducts(products, { category: activeCategory, query: searchQuery }),
      activeSort,
    )
  }, [products, activeCategory, searchQuery, activeSort])

  const hasActiveFilters = Boolean(activeCategory !== 'all' || searchQuery || activeSort !== 'featured')

  // ItemList JSON-LD — crawlers/AI answer engines can enumerate the catalog
  // without executing client-side fetches. Absolute URLs required (relative
  // paths are silently dropped by schema.org / OG parsers).
  const SITE_ORIGIN = 'https://shop.veterans.pro'
  const absUrl = (u: string) => (u.startsWith('http') ? u : `${SITE_ORIGIN}${u}`)
  const itemListJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: filtered.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${SITE_ORIGIN}/product/${p.slug}`,
      name: p.name,
      ...(p.images[0] ? { image: absUrl(p.images[0].url) } : {}),
    })),
  }), [filtered])

  function setParam(key: string, value: string | null) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value === null || value === '') {
        next.delete(key)
      } else {
        next.set(key, value)
      }
      return next
    })
  }

  function clearFilters() {
    setSearchParams({})
  }

  function handleCategoryToggle(category: CategoryId) {
    if (category === 'byom') return
    setSearchParams((prev) =>
      updateCatalogSearchParams(prev, {
        category: activeCategory === category ? 'all' : category,
      }),
    )
  }

  function handleSearchChange(value: string) {
    setSearchParams(
      (prev) => updateCatalogSearchParams(prev, { query: value }),
      { replace: true },
    )
  }

  const activeCategoryLabel = CATEGORIES.find((c) => c.id === activeCategory)?.label
  const pageTitle = activeCategory === 'all'
    ? searchQuery ? 'Search results' : 'Shop all products'
    : activeCategoryLabel ?? 'Products'

  return (
    <div>
      <title>Shop All — Veterans Merchandise | Shop.Veterans</title>
      <meta name="description" content="Browse embroidered caps, apparel, and gear honoring every branch of the U.S. Armed Forces. Veteran-owned, made to order in the USA." />
      <link rel="canonical" href={`${SITE_ORIGIN}/products`} />
      <meta property="og:title" content="Shop All — Veterans Merchandise | Shop.Veterans" />
      <meta property="og:description" content="Browse embroidered caps, apparel, and gear honoring every branch of the U.S. Armed Forces. Veteran-owned, made to order in the USA." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_ORIGIN}/products`} />
      <meta property="og:image" content={`${SITE_ORIGIN}/og-image.png`} />
      <meta name="twitter:card" content="summary_large_image" />
      {filtered.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListJsonLd) }}
        />
      )}

      <div className="max-w-6xl mx-auto px-6 py-12">
        {/* ── Header ── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight mb-1">{pageTitle}</h1>
            {!isLoading && !isError && (
              <p className="text-sm text-white/50">
                {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
                {searchQuery ? ` matching "${searchQuery}"` : ''}
              </p>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center lg:justify-end">
            <div className="relative min-w-0 sm:min-w-80">
              <label htmlFor="product-search" className="sr-only">
                Search products
              </label>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/40"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3.5-3.5" />
              </svg>
              <input
                id="product-search"
                type="search"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search by product name"
                className="w-full rounded-lg border border-white/20 bg-navy-deep/80 py-2.5 pl-10 pr-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-gold"
              />
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <label htmlFor="sort" className="text-sm text-white/60 font-medium whitespace-nowrap">
                Sort by
              </label>
              <select
                id="sort"
                value={activeSort}
                onChange={(e) => setParam('sort', e.target.value === 'featured' ? null : e.target.value)}
                className="bg-navy-deep border border-white/20 rounded-md px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-gold"
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Category bar ── */}
        <div className="mb-8 space-y-3">
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-xs text-white/60 font-semibold uppercase tracking-wider shrink-0">
              Category
            </span>
            {CATEGORIES.map((category) => {
              if (category.id === 'byom') {
                return (
                  <Link
                    key={category.id}
                    to="/design"
                    title={category.hint}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold border border-white/20 text-white/70 hover:border-gold/50 hover:text-white transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold whitespace-nowrap"
                  >
                    {category.label}
                  </Link>
                )
              }
              if (!isCountedCategory(category.id)) return null
              return (
                <CategoryChip
                  key={category.id}
                  label={category.label}
                  count={chipCounts[category.id]}
                  title={category.hint}
                  active={activeCategory === category.id}
                  onToggle={() => handleCategoryToggle(category.id)}
                />
              )
            })}
          </div>

          {hasActiveFilters && (
            <div className="pt-1">
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs text-gold-text hover:text-gold-bright underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>

        {/* ── States ── */}
        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="rounded-lg border border-gold/20 bg-navy/40 h-64 animate-pulse" />
            ))}
          </div>
        )}

        {isError && (
          <p className="text-red-400">Could not load products. Please refresh.</p>
        )}

        {!isLoading && !isError && filtered.length === 0 && products.length === 0 && (
          <p className="text-white/60">No products available yet.</p>
        )}

        {!isLoading && !isError && filtered.length === 0 && products.length > 0 && (
          <div
            className="rounded-xl border border-white/10 bg-navy/30 p-12 text-center"
            role="status"
            aria-live="polite"
          >
            <p className="text-white/60 mb-4">No products match the current filters.</p>
            <button
              type="button"
              onClick={clearFilters}
              className="text-sm text-gold-text hover:text-gold-bright underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold rounded"
            >
              Clear filters
            </button>
          </div>
        )}

        {!isLoading && !isError && filtered.length > 0 && (
          <div
            id="product-grid"
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6"
            role="region"
            aria-live="polite"
            aria-label={`${filtered.length} products`}
          >
            {filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </div>

      <TrustStrip className="mt-12" />
    </div>
  )
}
